import { ref, reactive, h } from 'vue';
import axios from 'axios';
import baseMessages from './messages';
import documentMessages from './documentMessages';
const messages = { ...baseMessages, ...documentMessages };
import terms from './terms';

const supported = ['en', 'es', 'ko'];
export const language = ref('en');
export const languageState = reactive({ loading: false, saving: false, loaded: false, accountId: null, error: '' });
let portal = 'client', loadedToken, inFlight, loadSequence = 0;
export const languages = [{ value: 'en', label: 'English' }, { value: 'es', label: 'Español (Spanish)' }, { value: 'ko', label: '한국어 (Korean)' }];
function applyLanguage(value) {
  if (!supported.includes(value)) return;
  language.value = value; document.documentElement.lang = value;
}
const endpoint = () => `${process.env.VUE_APP_BACKEND_DOMAIN}/api/${portal}-language`;
const options = token => ({ headers: { Authorization: `Bearer ${token}` }, timeout: 10000 });
// Coordinators and retired Vendor sessions must not access Client preferences.
const supportsAccountLanguage = () => portal !== 'client' || !['vendor', 'coordinator'].includes(localStorage.getItem('user_type'));
function resetAccountLanguage() {
  loadSequence++; loadedToken = null; inFlight = null;
  applyLanguage('en');
  Object.assign(languageState, { accountId: null, loaded: true, loading: false, saving: false, error: '' });
}
export async function syncLanguage(force = false) {
  const token = localStorage.getItem('token');
  if (!token || !supportsAccountLanguage()) { resetAccountLanguage(); return; }
  if (!force && loadedToken === token && languageState.loaded) return;
  if (!force && inFlight?.token === token) return inFlight.promise;
  if (token !== loadedToken) { applyLanguage('en'); languageState.accountId = null; languageState.loaded = false; }
  const sequence = ++loadSequence; languageState.loading = true; languageState.error = '';
  const promise = axios.get(endpoint(), options(token)).then(({ data }) => {
    if (token !== localStorage.getItem('token') || sequence !== loadSequence || !supportsAccountLanguage()) return;
    if (data.portal !== portal || !supported.includes(data.language) || !data.account_id) throw new Error('Invalid language response.');
    loadedToken = token; applyLanguage(data.language); languageState.accountId = data.account_id; languageState.loaded = true;
  }).catch(() => {
    if (token === localStorage.getItem('token') && sequence === loadSequence) { languageState.loaded = false; languageState.error = 'Could not load your account language. Please retry.'; }
  }).finally(() => { if (sequence === loadSequence) languageState.loading = false; if (inFlight?.promise === promise) inFlight = null; });
  inFlight = { token, promise }; return promise;
}
export async function setLanguage(value) {
  const token = localStorage.getItem('token');
  if (!token || !supportsAccountLanguage()) { resetAccountLanguage(); return false; }
  if (!supported.includes(value) || languageState.saving || !languageState.loaded || !languageState.accountId) return false;
  if (token !== loadedToken) return false;
  languageState.saving = true; languageState.error = '';
  try {
    const { data } = await axios.put(endpoint(), { language: value }, options(token));
    if (token !== localStorage.getItem('token') || !supportsAccountLanguage()) return false;
    if (data.portal !== portal || data.account_id !== languageState.accountId || !supported.includes(data.language)) throw new Error('Invalid language response.');
    applyLanguage(data.language); return true;
  } catch (_) { if (token === localStorage.getItem('token')) Object.assign(languageState, { error: 'Could not save your account language. Your previous setting is unchanged.' }); return false; }
  finally { Object.assign(languageState, { saving: false }); }
}
const normalize = value => String(value ?? '').replace(/\s+/g, ' ').trim();
const canonical = value => normalize(value).toLowerCase().replace(/[.!:]$/, '').replace(/’/g, "'");
const entries = new Map();
for (const [key, value] of Object.entries(messages)) {
  const id = canonical(key);
  if (!entries.has(id) || normalize(key).toLowerCase() === id) entries.set(id, value);
}
export function t(message, values = []) {
  const key = normalize(message), entry = messages[key] || entries.get(canonical(key));
  const text = language.value === 'en' ? key : entry?.[language.value] || key;
  return text.replace(/\{(\d+)\}/g, (match, index) => values[index] == null ? match : String(values[index]));
}
const patterns = Object.keys(messages).filter(key => /\{\d+\}/.test(key)).map(key => ({ key,
  regex: new RegExp('^' + key.split(/\{\d+\}/).map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('(.+?)') + '$'),
}));
export function systemMessage(value) {
  if (typeof value !== 'string') return value;
  if (value.includes('\n')) return value.split('\n').map(systemMessage).join('\n');
  const key = normalize(value);
  if (messages[key] || entries.has(canonical(key))) return t(key);
  const validation = key.match(/^The (.+?) (field is required|must be a valid email address|must be a number|must be at least (\d+) characters|must not be greater than (\d+) characters|must not be greater than (\d+) kilobytes|confirmation does not match)\.?$/);
  if (validation) {
    const field = t(validation[1].replace(/_/g, ' '));
    const rule = validation[2].replace(/\d+/, '{1}');
    return t('The {0} ' + rule + '.', [field, validation[3] || validation[4] || validation[5]]);
  }
  const required = key.match(/^(.*?) (?:are|is) required\.?$/);
  if (required) return t('Please complete: {0}', [required[1].split(',').map(field => t(field.trim())).join(', ')]);
  for (const pattern of patterns) { const match = key.match(pattern.regex); if (match) return t(pattern.key, match.slice(1)); }
  return value; // Unknown server text and user content are never machine-translated.
}
const chunkCache = new Map();
function chunks(message) {
  const key = normalize(message), cacheKey = language.value + '\n' + key;
  if (chunkCache.has(cacheKey)) return chunkCache.get(cacheKey);
  const entry = messages[key] || entries.get(canonical(key));
  const text = language.value === 'en' ? key : entry?.[language.value] || key;
  const glossary = language.value === 'en' ? [] : terms[language.value];
  const result = []; let last = 0;
  const tokens = new RegExp('\\{(\\d+)\\}' + (glossary.length ? '|' + glossary.map(term => term[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') : ''), 'gi');
  for (const match of text.matchAll(tokens)) {
    if (match.index > last) result.push({ text: text.slice(last, match.index) });
    if (match[1] !== undefined) result.push({ parameter: Number(match[1]) });
    else result.push({ text: match[0], english: glossary.find(term => term[0].toLowerCase() === match[0].toLowerCase())[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length) result.push({ text: text.slice(last) });
  if (chunkCache.size > 2500) chunkCache.clear(); chunkCache.set(cacheKey, result); return result;
}
export function PortalText(props, context) {
  const slots = context?.slots || {};
  const values = props.values || [];
  return chunks(props.message).map((chunk, index) => {
    if (chunk.parameter !== undefined) return slots['p' + chunk.parameter] ? slots['p' + chunk.parameter]() : values[chunk.parameter] == null ? '' : String(values[chunk.parameter]);
    if (!chunk.english || props.help === false) return chunk.text;
    if (props.annotate) return h('ruby', { key: index, class: 'portal-guide-term' }, [h('span', chunk.text), h('rt', chunk.english)]);
    return h('abbr', { key: index, title: chunk.english, 'data-english-term': chunk.english, class: 'portal-language-term', tabindex: 0 }, chunk.text);
  });
}
PortalText.props = ['message', 'values', 'help', 'annotate'];
export function installLanguage(app, Swal, configuration = {}) {
  if (!app.config.globalProperties.$t) {
    app.config.globalProperties.$t = t; app.config.globalProperties.$message = systemMessage;
    app.component('PortalText', PortalText); document.documentElement.lang = language.value;
    if (configuration.portal) portal = configuration.portal;
    configuration.router?.beforeEach(async () => { await syncLanguage(); });
    window.addEventListener('storage', event => { if (event.key === 'token') syncLanguage(true); });
  }
  const originalFire = Swal.fire;
  Swal.fire = function (...args) {
    const translateOptions = options => {
      const translated = { ...options };
      for (const key of ['title','text','inputLabel','inputPlaceholder','confirmButtonText','cancelButtonText','denyButtonText','validationMessage','closeButtonAriaLabel'])
        if (typeof translated[key] === 'string') translated[key] = systemMessage(translated[key]);
      if (!translated.confirmButtonText) translated.confirmButtonText = t('OK');
      if (!translated.cancelButtonText) translated.cancelButtonText = t('Cancel');
      if (typeof translated.inputValidator === 'function') { const validate = translated.inputValidator; translated.inputValidator = async (...values) => systemMessage(await validate(...values)); }
      return translated;
    };
    return typeof args[0] === 'object' ? originalFire.call(this, translateOptions(args[0])) : originalFire.call(this, translateOptions({ title: systemMessage(args[0]), text: systemMessage(args[1]), icon: args[2] }));
  };
}
